/* eslint-disable @typescript-eslint/no-explicit-any */
// unfortunately konva doesn't provide types for its events
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Stage, Layer, Line } from 'react-konva';
import uuid from 'uuid-random';
import { StyledDrawingPanel } from '../Styled';
import { DrawingContext } from '../../contexts/drawingContext';
import { LineType } from '../../types';
import pen from '../../assets/icons/cursors/Pen.png';
import { clone } from 'lodash';

interface IDrawingPanel {
  mode: string;
  isAccessible: boolean;
  exemplarDimensions: {
    width: number;
    height: number;
  };
}

export const DrawingPanel: React.FC<IDrawingPanel> = (props): JSX.Element => {
  const tool = 'pen';
  const { mode, isAccessible, exemplarDimensions } = props;
  const { width, height } = exemplarDimensions;
  const { lines, setLines, isPen, drawingColor, areDrawingsHidden, saveDrawingUpdates } = useContext(DrawingContext);
  const [isInMove, setInMove] = useState(false);
  const isDrawing = useRef(false);

  useEffect(() => {
    if (!Array.isArray(lines[mode])) {
      setLines({ ...lines, [mode]: [] });
    }
  }, [mode]);

  const handleStart = (event: any) => {
    if (isPen) {
      event.evt.preventDefault();
      isDrawing.current = true;
      const pos = event.target.getStage().getPointerPosition();
      setLines({ ...lines, [mode]: [...lines[mode], { tool, drawingColor, points: [pos.x, pos.y] }] });
    }
  };

  const handleMove = (event: any) => {
    if (!isDrawing.current) {
      return;
    }

    setInMove(true);
    event.evt.preventDefault();
    const stage = event.target.getStage();
    const point = stage.getPointerPosition();
    const lastLine = lines[mode][lines[mode].length - 1];

    lastLine.points = lastLine.points.concat([point.x, point.y]);

    lines[mode].splice(lines[mode].length - 1, 1, lastLine);
    setLines(getConcatenatedLines());
  };

  const handleStop = () => {
    isDrawing.current = false;
  };

  const getConcatenatedLines = () => {
    return { ...lines, ...{ [mode]: lines[mode].concat() } };
  };

  const handleMouseUp = () => {
    handleStop();
    const prevState = clone(getConcatenatedLines());
    prevState[mode].pop();
    isInMove && saveDrawingUpdates(prevState, lines);
    setInMove(false);
  };

  return (
    <StyledDrawingPanel cursor={pen as string} isPen={isPen} isAccessible={isAccessible} data-testId="drawing-panel">
      <Stage
        width={width}
        height={height}
        onMouseDown={handleStart}
        onMousemove={handleMove}
        onMouseup={handleMouseUp}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleMouseUp}
        onMouseOut={handleStop}
      >
        <Layer width={width} height={height}>
          {lines[mode] &&
            !areDrawingsHidden &&
            lines[mode].map((line: LineType) => (
              <Line
                key={uuid()}
                points={line.points}
                stroke={line.drawingColor}
                strokeWidth={5}
                tension={0.5}
                lineCap="round"
                globalCompositeOperation={'source-over'}
              />
            ))}
        </Layer>
      </Stage>
    </StyledDrawingPanel>
  );
};
